import styles from "./MarkAsCompleteModal.module.css";
import { Modal, Button } from "@shopify/polaris";
import { AssetType } from "../../types/AssetType";
import { ElementImportance } from "../../types/ElementImportance";
import { useDispatch, useSelector } from "react-redux";
import { updateSurveyorCompletionStatus } from "../../store/actions/MarkSectionComplete";
import { useTranslation } from "react-i18next";
import {InspectionSentences, VesselContactSentences} from "../../config/SystemMessage";
import { PortalState } from "../../types/PortalState";
import { ReactElement, useState } from "react";
import InspectionElement, { PdfChecklistValidationType } from "../../types/InspectionElement";
import {makeRequest} from "../../services/UploadService";
import {Auth} from "aws-amplify";
import { toast } from "react-toastify";
import AsimsApiResponse from "../../types/AsimsApiResponse";
import {userIsDocCollector} from "../../helpers/users";
import {getReUseSectionsWithInvalidDates} from "../../types/ReUseAsset";
import {Inspection} from "../../types/Inspection";


const DOC_COLLECTOR_COMPLETE_ENDPOINT = "/doccollectioncompletion";

interface Props {
    type: number;
    active: boolean;
    closeModal: () => void;
    appChecklistRequired?: boolean;
    appChecklistSubmitted?: boolean;
}

export default function MarkAsCompleteModal({
    type,
    active,
    closeModal,
    appChecklistRequired,
    appChecklistSubmitted
}: Props): ReactElement {
    const dispatch = useDispatch();
    const { t } = useTranslation("common");
    const [text, setText] = useState("");

    const currentInspection = useSelector(
        (state: PortalState) => state.portal.activeInspection
    );

    const isDocumentCollector: boolean = useSelector((state: any) =>  userIsDocCollector(state));


    let invalidElements = [""];
    let currentSection = "";
    let warningText = "";
    let firstParagraph = "";
    let secondParagraph = "";
    let thirdParagraph = "";
    let title = "";
    let buttonText = "";

    const setupSurveyorModal = () => {
        if (currentInspection !== undefined) {

            if (type === AssetType.CheckList) {
                if (!currentInspection?.SurveyorCompletionStatus?.EmbarkDate) {
                    invalidElements.push("Embark date and time");
                }
    
                if (!currentInspection?.SurveyorCompletionStatus?.DisembarkDate) {
                    invalidElements.push("Disembark date and time");
                }

                if (appChecklistRequired && !appChecklistSubmitted) {
                    invalidElements.push(t('appChecklist.notSubmittedWarning'));
                }
            }

            currentInspection?.sections
                .filter(e => e.ElementType === type && e.MinItems != null && e.MaxItems != null)
                .forEach((element) => isElementValid(element));

            if (type === AssetType.CarbonOffsetting && currentInspection.CarbonSectionData !== undefined) {
                if (!currentInspection.CarbonSectionData.AeroplaneSection.Complete) {
                    invalidElements.push("Aeroplane");
                } else if (!currentInspection.CarbonSectionData.CarSection.Complete) {
                    invalidElements.push("Car");
                } else if (!currentInspection.CarbonSectionData.TrainSection.Complete) {
                    invalidElements.push("Train");
                }
            }
        }

        currentSection = getCurrentSection(type) ?? "";

        title = t(InspectionSentences.MARK_COMPLETE_MODAL_TITLE);

        warningText = t(InspectionSentences.MARK_COMPLETE_MODAL_WARNING_MESSAGE);
        warningText = warningText.replace("{currentSection}", currentSection.toLowerCase());

        firstParagraph = t(InspectionSentences.MARK_COMPLETE_MODAL_FIRST_PARGRAPH);
        firstParagraph = firstParagraph.replace("{currentSection}", currentSection);

        secondParagraph = t(InspectionSentences.MARK_COMPLETE_MODAL_SECOND_PARGRAPH);
        secondParagraph = secondParagraph.replace("{currentSection}", currentSection.toLowerCase());

        thirdParagraph = t(InspectionSentences.MARK_COMPLETE_MODAL_THIRD_PARGRAPH);
        thirdParagraph = thirdParagraph.replace("{currentSection}", currentSection.toLowerCase());

        buttonText = t(InspectionSentences.MARK_COMPLETE_MODAL_BUTTON_TEXT);
    }

    const setupDocumentCollectionModal = () => {
        currentSection = getCurrentSection(type) ?? "";

        if (currentInspection !== undefined) {
            currentInspection?.sections
            .filter(e => e.ElementType === type && e.MinItems !== null && e.MaxItems !== null)
            .forEach((element) => isElementValid(element));
        }

        title = t(VesselContactSentences.MARK_COMPLETE_VC_MODAL_TITLE);

        warningText = t(VesselContactSentences.MARK_COMPLETE_VC_MODAL_WARNING_MESSAGE);
        warningText = warningText.replace("{currentSection}", currentSection.toLowerCase());

        firstParagraph = t(VesselContactSentences.MARK_COMPLETE_VC_MODAL_FIRST_PARAGRAPH);
        secondParagraph = t(VesselContactSentences.MARK_COMPLETE_VC_MODAL_SECOND_PARAGRAPH);

        buttonText = t(VesselContactSentences.MARK_COMPLETE_VC_MODAL_BUTTON_TEXT);
    }

    if (isDocumentCollector) {
        setupDocumentCollectionModal();
    } else {
        setupSurveyorModal();
    }

    invalidElements = invalidElements.filter((name) => {
        return name !== "";
    });

    const invalidElementsHtml = invalidElements.map((name, i) => {
        if (invalidElements.length === 1 || i === invalidElements.length - 1) {
            return <strong key={name}>{name + "."}</strong>;
        }
        return <strong>{name + ", "}</strong>;
    });

    invalidElementsHtml.unshift(
        <p key={warningText} className={styles.elementsWarningText}>
            {warningText}
        </p>
    );

    async function handleMarkAsComplete() {
        if (isDocumentCollector) {
            handleDocumentCollectionComplete();
        } else {
            handleSurveyorComplete();
        }

        closeModal();
    }

    function handleSurveyorComplete() {
        const surveyorCompletionStatus = currentInspection?.SurveyorCompletionStatus;

        let invalidReUseSections : string[] = [];
        if (surveyorCompletionStatus != null) {
            switch (type) {
                case AssetType.CheckList:
                    surveyorCompletionStatus.ChecklistComplete = true;
                    surveyorCompletionStatus.ChecklistComment = text;
                    break;
                case AssetType.Document:
                    surveyorCompletionStatus.DocumentsComplete = true;
                    surveyorCompletionStatus.DocumentsComment = text;
                    invalidReUseSections = getReUseSectionsWithInvalidDates(currentInspection as Inspection);
                    break;
                case AssetType.Image:
                    surveyorCompletionStatus.PhotosComplete = true;
                    surveyorCompletionStatus.PhotosComment = text;
                    break;
                case AssetType.CarbonOffsetting:
                    surveyorCompletionStatus.CarbonSectionComplete = true;
                    surveyorCompletionStatus.CarbonSectionComment = text;
                    break;
            }

            dispatch(updateSurveyorCompletionStatus(surveyorCompletionStatus, type, invalidElements,invalidReUseSections));
        }
    }

    async function handleDocumentCollectionComplete() {
        const currentSession = await Auth.currentSession();
        const idToken = currentSession.getIdToken().getJwtToken();

        const result: AsimsApiResponse = await makeRequest(
            {
                url: DOC_COLLECTOR_COMPLETE_ENDPOINT,
                method: "post",
                data: {
                    InspectionId: currentInspection?.Id.toLowerCase(),
                    invalidElements: invalidElements,
                    type
                }
            },
            idToken
        );

        if (!result.success) {
            toast.error(t(VesselContactSentences.MARK_COMPLETE_VC_SUBMISSION_ERROR));
        } else {
            toast.success(t(VesselContactSentences.MARK_COMPLETE_VC_SUBMISSION_SUCCESS));
        }
    }

    const updateText = (event: any) => {
        setText(event.target.value);
    };

    function getCurrentSection(assetType: number) {
        switch (assetType) {
            case AssetType.CheckList:
                return "Inspection Checklists";
            case AssetType.Document:
                return "Documents";
            case AssetType.Image:
                return "Photos";
            case AssetType.CarbonOffsetting:
                return "Carbon Offsetting information";
            default:
                throw new Error("Unexpected asset type: ${assetType}");
        }
    }

    function isElementValid(element: InspectionElement) {
        // If this is an AppChecklist element and the app checklist is active, skip — handled separately
        if (element.ElementType === AssetType.CheckList && element.PdfChecklistValidation === PdfChecklistValidationType.AppChecklist && appChecklistRequired) {
            return;
        }

        let elementItems = [];
        switch (element.ElementType) {
            case AssetType.CheckList:
                elementItems = element.checkList.assets;
                break;
            case AssetType.Document:
                elementItems = element.Documents;
                break;
            case AssetType.Image:
                elementItems = element.Images;
                break;
        }

        if (element.ElementImportance !== ElementImportance.Optional &&
            (element.MinItems > elementItems.length || element.MaxItems < elementItems.length)) {
            invalidElements.push(element.Name);
        }
    }

    return (
        <Modal
            open={active}
            onClose={closeModal}
            title={title ?? t(InspectionSentences.MARK_COMPLETE_MODAL_TITLE)}
        >
            <Modal.Section>
                <p data-cy="markCompleteModalBody">{firstParagraph}</p>
                {invalidElements.length !== 0 ? (
                    <div className={styles.invalidElementsContainer}>
                        <div className={styles.invalidElementsList}>
                            <p className={styles.invalidElements}>
                                {invalidElementsHtml}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div />
                )}
                {thirdParagraph
                    ? <p>
                        {secondParagraph}
                        <br />
                        <br />
                        {thirdParagraph}
                    </p>
                    : <p>
                        {secondParagraph}
                    </p>
                }

                <div className={styles.commentBox}>
                    <textarea
                        onChange={updateText}
                        placeholder="Comments"
                        data-cy="markCompleteModalInput"
                    />
                </div>
                <div className={styles.buttonContainer}>
                    <Button onClick={closeModal} size="large">
                        {" "}
                        Go Back{" "}
                    </Button>
                    <Button size="large" onClick={handleMarkAsComplete} primary>
                        {buttonText}
                    </Button>
                </div>
            </Modal.Section>
        </Modal>
    );
}
